import type { ParticipantPropertiesPayload } from '@zoom/videosdk'
import { useEffect, useRef, useCallback } from 'react'
import { ZoomSDKClient, ZoomParticipant } from 'zoom/types'
import { rollbarService, RollbarExceptionNames } from 'services/rollbar'
import { useMount } from './useMount'

const useParticipantsChange = (
    client: ZoomSDKClient | undefined,
    fn: (participants: ZoomParticipant[], currentUpdates?: ParticipantPropertiesPayload[]) => void
): void => {
    const fnRef = useRef(fn)
    fnRef.current = fn
    const currentUserIdRef = useRef<number>()

    useEffect(() => {
        if (client) {
            try {
                currentUserIdRef.current = client.getSessionInfo()?.userId
            } catch (error) {
                rollbarService.rollbar.error(RollbarExceptionNames.SubsessionGetSessionInfoFailed, {
                    error: error instanceof Error ? error.message : String(error)
                })
            }
        }
    }, [client])
    const callback = useCallback(
        (items?: ParticipantPropertiesPayload[]) => {
            if (client) {
                const participants = client.getAllUser() ?? []
                const currentUserUpdateItems = items?.filter(item => item.userId === currentUserIdRef.current)
                fnRef.current?.(participants, currentUserUpdateItems)
            }
        },
        [client]
    )

    useEffect(() => {
        if (client) {
            client.on('user-added', callback)
            client.on('user-removed', callback)
            client.on('user-updated', callback)
            return () => {
                client.off('user-added', callback)
                client.off('user-removed', callback)
                client.off('user-updated', callback)
            }
        }
    }, [client, callback])

    useMount(() => {
        callback()
    })
}

export { useParticipantsChange }
